import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot, doc, updateDoc, deleteDoc, writeBatch, getDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useFirebase } from './FirebaseContext';
import InAppNotificationBanner from '../components/InAppNotificationBanner';

const NotificationsContext = createContext();

export function NotificationsProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const { db, auth } = useFirebase();
  const [unsubscribe, setUnsubscribe] = useState(null);
  const navigate = useNavigate();
  // iOS NotificationManager.playNotificationSound: customer-alert on each new
  // notification after the initial load, plus an in-app banner.
  const initialLoadRef = useRef(true);
  const lastNotifIdRef = useRef(null);
  const alertAudioRef = useRef(null);
  // Only customers alert here — driver/restaurant portals run their own
  // listeners with their own sounds (driver-alert / cash-register).
  const isCustomerRef = useRef(true);
  const [bannerNotif, setBannerNotif] = useState(null);

  // Load notifications when user changes
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setupNotifications(user.uid);
      } else {
        cleanup();
      }
    });

    return () => {
      unsubscribeAuth();
      cleanup();
    };
  }, [auth]);

  const setupNotifications = async (userId) => {
    if (unsubscribe) {
      unsubscribe();
    }

    setLoading(true);
    initialLoadRef.current = true;
    lastNotifIdRef.current = null;

    // Role gate: this provider wraps every route, so drivers/restaurants would
    // otherwise hear the customer sound for their own notifications.
    try {
      const snap = await getDoc(doc(db, 'users', userId));
      isCustomerRef.current = !snap.exists() || (snap.data().role || 'customer') === 'customer';
    } catch {
      isCustomerRef.current = true;
    }
    // Single-equality query only — no composite index exists for
    // userId+createdAt, and iOS itself avoids it by client-side filtering.
    // Sort happens in memory after fetch.
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      limit(200)
    );

    const unsubscribeListener = onSnapshot(q, (snapshot) => {
      const notificationsData = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          expiresAt: doc.data().expiresAt?.toDate()
        }))
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, 50);
      setNotifications(notificationsData);
      setUnreadCount(notificationsData.filter(n => !n.isRead).length);
      // iOS: play the customer alert + drop an in-app banner when a new
      // notification arrives (tracked by newest doc id — not on the initial
      // snapshot).
      const newest = notificationsData[0];
      if (!initialLoadRef.current && newest && newest.id !== lastNotifIdRef.current && isCustomerRef.current) {
        try {
          if (!alertAudioRef.current) alertAudioRef.current = new Audio('/sounds/customer-alert.m4a');
          alertAudioRef.current.currentTime = 0;
          alertAudioRef.current.play().catch(() => {});
        } catch { /* audio blocked until user interaction */ }
        setBannerNotif(newest);
      }
      initialLoadRef.current = false;
      lastNotifIdRef.current = newest?.id || lastNotifIdRef.current;
      setLoading(false);
    }, (err) => {
      setError(err.message);
      setLoading(false);
    });

    setUnsubscribe(() => unsubscribeListener);
  };

  const cleanup = () => {
    if (unsubscribe) {
      unsubscribe();
      setUnsubscribe(null);
    }
    setNotifications([]);
    setUnreadCount(0);
  };

  const markAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        isRead: true
      });
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const batch = writeBatch(db);
      const unreadNotifications = notifications.filter(n => !n.isRead);

      unreadNotifications.forEach(notification => {
        const notificationRef = doc(db, 'notifications', notification.id);
        batch.update(notificationRef, { isRead: true });
      });

      await batch.commit();
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const value = {
    notifications,
    loading,
    error,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <InAppNotificationBanner
        notification={bannerNotif}
        onTap={() => {
          const n = bannerNotif;
          setBannerNotif(null);
          if (n?.id) navigate(`/notifications/${n.id}`);
        }}
        onDismiss={() => setBannerNotif(null)}
      />
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}
